import { useEffect, useContext } from 'react';
import { Grid, InputAdornment } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttDialog,
    SttFormText,
    SelectGroupField,
} from '../../../sporttia/all';
import { useCrud } from '../../../lib/hooks';
import { PERMISSIONS } from '../../../lib/permissions';

type Category = {
    id?: string | number;
    name: string;
    description?: string;
    enrollment?: number | string;
    maxParticipants?: number | string;
    maxTeams?: number | string;
    mandatory?: {
        id: number;
        name: string;
    } | null;
    trash?: boolean;
};

type Props = {
    item?: Category;
    eventName: string;
    teamable: boolean;
    onClose: () => void;
    onSave: (category: Category) => void;
    onDelete: (category: Category) => void;
    onRecover: (category: Category) => void;
};

export default function ModalCategory({
    item,
    eventName,
    teamable,
    onClose,
    onSave,
    onDelete,
    onRecover,
}: Props): React.ReactElement | null {
    const [category, setCategory, setProperty] = useCrud<Category>(
        item || ({} as Category),
    );

    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    useEffect(() => {
        if (item) {
            setCategory(item);
        }
    }, [item, setCategory]);

    if (!category) {
        return null;
    }

    return (
        <SttDialog
            title={t('Category')}
            subtitle={eventName}
            maxWidth="sm"
            open={!!item}
            onClose={onClose}
            content={
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                        <SttFormText
                            caption={t('Name')}
                            name="name"
                            defVal={category.name ?? null}
                            onChange={({ name, value }) =>
                                setProperty({
                                    name: name as keyof Category,
                                    value: value as string,
                                })
                            }
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <SttFormText
                            caption={t('Description')}
                            name="description"
                            defVal={category.description ?? null}
                            onChange={({ name, value }) =>
                                setProperty({
                                    name: name as keyof Category,
                                    value: value as string,
                                })
                            }
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <SttFormText
                            caption={t('Inscription')}
                            name="enrollment"
                            defVal={category.enrollment?.toString() ?? null}
                            onChange={({ name, value }) =>
                                setProperty({
                                    name: name as keyof Category,
                                    value: value as string | number,
                                })
                            }
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {cxt?.sc?.city?.country?.currencySymbol
                                            ? cxt?.sc?.city?.country
                                                  ?.currencySymbol
                                            : '€'}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        {teamable ? (
                            <SttFormText
                                caption={t('MaxTeams')}
                                name="maxTeams"
                                defVal={category.maxTeams?.toString() ?? null}
                                onChange={({ name, value }) =>
                                    setProperty({
                                        name: name as keyof Category,
                                        value: value as string | number,
                                    })
                                }
                            />
                        ) : (
                            <SttFormText
                                caption={t('MaxParticipants')}
                                name="maxParticipants"
                                defVal={
                                    category.maxParticipants?.toString() ?? null
                                }
                                onChange={({ name, value }) =>
                                    setProperty({
                                        name: name as keyof Category,
                                        value: value as string | number,
                                    })
                                }
                            />
                        )}
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <SelectGroupField
                            caption={t('MandatoryGroup')}
                            name="mandatory"
                            defVal={category.mandatory}
                            onChange={({ name, value }) =>
                                setProperty({
                                    name: name as keyof Category,
                                    value: value as {
                                        id: number;
                                        name: string;
                                    } | null,
                                })
                            }
                            onlyActive
                        />
                    </Grid>
                </Grid>
            }
            buttons={[
                {
                    type: 'save',
                    onClick: () => onSave(category),
                    permission: PERMISSIONS.EVENT.MODIFY,
                },
                category.id
                    ? category.trash
                        ? {
                              type: 'recover',
                              onClick: () => onRecover(category),
                              permission: PERMISSIONS.EVENT.MODIFY,
                          }
                        : {
                              type: 'delete',
                              onClick: () => onDelete(category),
                              permission: PERMISSIONS.EVENT.MODIFY,
                          }
                    : false,
            ]}
        />
    );
}
